import React, { useState, useMemo, useCallback } from 'react';
import { Button, Typography, Avatar, Input, Tag, Flex, message, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, MessageOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import parsePhoneNumber from 'libphonenumber-js';
import { FixedSizeList as List } from 'react-window';
import { getFlag } from '../../../utils/flag';
import { formatPhoneNumber, isValidShortCodeSms, phoneNumberFormatE164, phoneNumberValidate } from '../../../utils/phoneLib';
import { Contact } from '@data-phone/react-generic/dist/components/Forms/Contacts';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { formatContactName } from '../../../utils/contacts';
import ModalWrapper from '../../generic/modal';

type MyContact = Contact & {
    searchableName: string;
};

const { Text } = Typography;

interface Props {
    onParticipantsSubmit?: (phoneNumbers: string[]) => void;
}

const MAX_GROUP_RECIPIENTS = 20;
const BRAND = '#653BFA';

const participantTagStyle: React.CSSProperties = {
    margin: 0,
    borderRadius: 999,
    padding: '2px 8px 2px 4px',
    background: '#F3F0FF',
    color: '#4C2EC9',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '22px',
    border: 'none',
};

const ContactItem = React.memo(({ index, style, data }: any) => {
    const { contacts, onContactSelect, participants } = data;
    const contact = contacts[index];
    const [hovered, setHovered] = useState(false);

    if (!contact) return null;

    const name = formatContactName(contact);
    const phone = contact.phones[0]?.phone_number || '';
    const normalizedPhone = phoneNumberFormatE164(phone);
    const isSelected = Boolean(normalizedPhone && participants.includes(normalizedPhone));

    return (
        <div
            style={{
                ...style,
                boxSizing: 'border-box',
                padding: '8px 16px',
                cursor: isSelected ? 'default' : 'pointer',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                background: isSelected ? '#F3F0FF' : hovered ? '#f8f9fb' : '#ffffff',
                borderBottom: '1px solid #f1f3f4',
                transition: 'background 0.15s ease-out',
            }}
            onClick={() => !isSelected && onContactSelect(contact)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Avatar
                size={36}
                style={{
                    background: 'linear-gradient(135deg, rgb(101, 59, 250) 0%, rgb(126, 87, 251) 100%)',
                    marginRight: 12,
                }}
            >
                {name.charAt(0).toUpperCase()}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', lineHeight: '18px' }}>
                    {name}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: '16px' }}>
                    {formatPhoneNumber(phone)}
                </div>
            </div>
            {isSelected ? (
                <CheckOutlined style={{ color: BRAND, fontSize: 14 }} />
            ) : (
                <PlusOutlined style={{ color: hovered ? BRAND : '#9ca3af', fontSize: 14 }} />
            )}
        </div>
    );
});

const NewConversationModal: React.FC<Props> = ({ onParticipantsSubmit }) => {
    const [open, setOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [participants, setParticipants] = useState<string[]>([]);
    const [countryFlag, setCountryFlag] = useState('US');
    const { data: contactsRaw } = useSelector((state: RootState) => state.contacts);

    const contacts = useMemo(() => {
        return contactsRaw.map((contact: Contact) => {
            return {
                ...contact,
                searchableName: formatContactName(contact).toLowerCase(),
            }
        })
            .sort((a, b) => {
                const nameA = a.searchableName;
                const nameB = b.searchableName;
                return nameA.localeCompare(nameB);
            });
    }, [contactsRaw]);

    const contactByPhone = useMemo(() => {
        const map = new Map<string, MyContact>();
        for (const contact of contacts) {
            for (const phone of contact.phones) {
                const e164 = phoneNumberFormatE164(phone.phone_number);
                if (e164 && !map.has(e164)) {
                    map.set(e164, contact);
                }
            }
        }
        return map;
    }, [contacts]);

    const filteredContacts = useMemo(() => {
        if (!phoneNumber?.trim()) return contacts;

        const query = phoneNumber.toLowerCase().trim();
        const queryDigits = query.replace(/\D/g, '');
        const results = new Set<MyContact>();

        if (query.length >= 2) {
            for (const contact of contacts) {
                if (contact.searchableName.includes(query)) {
                    results.add(contact);
                }
            }

            if (queryDigits.length >= 3) {
                contacts.forEach((contact) => {
                    if (contact.phones.some(p => p.phone_number.includes(queryDigits))) {
                        results.add(contact);
                    }
                });
            }
        }

        return Array.from(results).sort((a, b) => {
            const nameA = a.searchableName;
            const nameB = b.searchableName;
            return nameA.localeCompare(nameB);
        });
    }, [phoneNumber, contacts]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        try {
            const parsed = parsePhoneNumber(value, 'US');
            if (parsed?.isValid()) {
                setPhoneNumber(parsed.country === 'US' ? parsed.formatNational() : parsed.number);
                setCountryFlag(parsed.country || 'US');
                return;
            }
        } catch { }

        setPhoneNumber(value);
        setCountryFlag(value.startsWith('+1') ? 'US' : 'US');
    }, []);

    const isValidParticipant = Boolean(
        phoneNumber && (phoneNumberValidate(phoneNumber) || isValidShortCodeSms(phoneNumber.trim()))
    );

    const addParticipant = useCallback((phone: string) => {
        const trimmed = phone.trim();
        if (!trimmed || (!phoneNumberValidate(trimmed) && !isValidShortCodeSms(trimmed))) return;

        const participant = isValidShortCodeSms(trimmed)
            ? trimmed
            : phoneNumberFormatE164(trimmed);
        if (!participant) return;

        if (participants.includes(participant)) {
            message.error('Number already added');
            return;
        }
        if (participants.length >= MAX_GROUP_RECIPIENTS) {
            message.error(`A group can have up to ${MAX_GROUP_RECIPIENTS} recipients`);
            return;
        }
        setParticipants((current) => [...current, participant]);
        setPhoneNumber('');
    }, [participants]);

    const handleSubmit = useCallback(() => {
        addParticipant(phoneNumber);
    }, [phoneNumber, addParticipant]);

    const handleContactSelect = useCallback((contact: Contact) => {
        addParticipant(contact.phones[0]?.phone_number || '');
    }, [addParticipant]);

    const startConversation = useCallback(() => {
        if (!participants.length) return;
        onParticipantsSubmit?.(participants);
        setOpen(false);
        setPhoneNumber('');
        setParticipants([]);
    }, [onParticipantsSubmit, participants]);

    const participantLabel = useCallback((phone: string) => {
        const contact = contactByPhone.get(phone);
        const name = contact ? formatContactName(contact) : '';
        return name || formatPhoneNumber(phone) || phone;
    }, [contactByPhone]);

    const itemData = useMemo(() => ({
        contacts: filteredContacts,
        onContactSelect: handleContactSelect,
        participants,
    }), [filteredContacts, handleContactSelect, participants]);

    return (
        <>
            <Tooltip title="New conversation">
                <Button
                    type="primary"
                    onClick={() => setOpen(true)}
                    icon={<PlusOutlined />}
                    aria-label="Start new conversation"
                    style={{
                        background: BRAND,
                        border: 'none',
                        borderRadius: 10,
                        minWidth: 36,
                        height: 36,
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                />
            </Tooltip>

            <ModalWrapper
                title="Start New Conversation"
                isModalOpen={open}
                setIsModalOpen={setOpen}
            >
                <Flex vertical gap={12} style={{ padding: '16px 20px 0' }}>
                    {participants.length > 0 && (
                        <Flex wrap="wrap" gap={8} align="center">
                            {participants.map((participant) => {
                                const contact = contactByPhone.get(participant);
                                const label = participantLabel(participant);
                                return (
                                    <Tag
                                        key={participant}
                                        className="new-conversation-tag"
                                        closable
                                        bordered={false}
                                        closeIcon={<CloseOutlined style={{ fontSize: 10, color: '#6b7280' }} />}
                                        onClose={() => setParticipants((current) => current.filter((item) => item !== participant))}
                                        style={participantTagStyle}
                                        title={formatPhoneNumber(participant) || participant}
                                    >
                                        <Avatar
                                            size={20}
                                            icon={!contact ? <UserOutlined /> : undefined}
                                            style={{ background: BRAND, fontSize: 10, flexShrink: 0 }}
                                        >
                                            {contact ? label.charAt(0).toUpperCase() : undefined}
                                        </Avatar>
                                        {label}
                                    </Tag>
                                );
                            })}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {participants.length}/{MAX_GROUP_RECIPIENTS}
                            </Text>
                        </Flex>
                    )}

                    <Input
                        size="large"
                        allowClear
                        className="new-conversation-input"
                        placeholder="Enter a phone number or search contacts"
                        value={phoneNumber}
                        onChange={handleInputChange}
                        onPressEnter={handleSubmit}
                        disabled={participants.length >= MAX_GROUP_RECIPIENTS}
                        prefix={
                            <img
                                style={{ height: 16, width: 24, borderRadius: 2, objectFit: 'cover' }}
                                src={getFlag(countryFlag)}
                                alt=""
                            />
                        }
                        suffix={
                            <PlusOutlined
                                onClick={isValidParticipant ? handleSubmit : undefined}
                                aria-label="Add recipient"
                                style={{
                                    color: isValidParticipant ? BRAND : '#d1d5db',
                                    cursor: isValidParticipant ? 'pointer' : 'not-allowed',
                                    fontSize: 16,
                                }}
                            />
                        }
                    />
                </Flex>

                <div style={{ padding: '16px 20px 8px' }}>
                    <Text style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                        {phoneNumber?.trim() ? `Found ${filteredContacts.length} contact${filteredContacts.length === 1 ? '' : 's'}` : 'All contacts'}
                    </Text>
                </div>

                <div style={{ flex: 1, background: '#ffffff', minHeight: 400 }}>
                    {filteredContacts.length > 0 ? (
                        <List
                            height={390}
                            width="100%"
                            itemCount={filteredContacts.length}
                            itemSize={64}
                            itemData={itemData}
                            overscanCount={3}
                        >
                            {ContactItem}
                        </List>
                    ) : phoneNumber?.trim() ? (
                        <div style={{ textAlign: 'center', padding: 80, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Text style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No contacts found</Text>
                            <Text style={{ color: '#6b7280', marginBottom: 24 }}>No contacts match "{phoneNumber}"</Text>
                            {isValidParticipant && (
                                <Button type="primary" onClick={handleSubmit} icon={<PlusOutlined />} style={{ background: BRAND, border: 'none' }}>
                                    Add recipient
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: 80 }}>
                            <Text style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, display: 'block' }}>Ready to start chatting?</Text>
                            <Text style={{ color: '#6b7280' }}>Enter a phone number above or select from your contacts</Text>
                        </div>
                    )}
                </div>
                <Flex justify="flex-end" style={{ padding: '12px 20px 16px' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<MessageOutlined />}
                        disabled={participants.length === 0}
                        onClick={startConversation}
                        style={{
                            borderRadius: 20,
                            background: participants.length ? BRAND : undefined,
                            border: 'none',
                            fontWeight: 600,
                            paddingInline: 20,
                        }}
                    >
                        {participants.length > 1 ? 'Start group conversation' : 'Start conversation'}
                    </Button>
                </Flex>
            </ModalWrapper>
        </>
    );
};

export default NewConversationModal;
